import times from 'lodash/times';

export const MAX_CHART_SIZE = 156;
export const CHART_OFFSET = 20;
export const CHART_SIZE_TOLERANCE = 0.03;

export const PRINT_MAX_CHART_SIZE = 100;

export interface Bucket {
  title: string;
  items: { value: number }[];
}

/**
 * # Bucket pie chart size algorithm
 *
 * - We have discrete chart sizes, staring from MAX_CHART_SIZE
 * - All other sizes are calculating subtracting CHART_OFFSET from the previous size
 * - The bucket with the biggest total gets the larger chart size and the other sizes are distributed accordingly
 * - If two buckets share the same total, or if the relative difference is smaller than CHART_SIZE_TOLERANCE, they will get the same chart size
 */
export const getBucketChartSizesDict = (buckets: Bucket[]) => {
  if (!buckets || buckets.length === 0) {
    throw new Error('Pass at least one bucket');
  }

  const chartSizes = times(buckets.length).map(i => MAX_CHART_SIZE - i * CHART_OFFSET);

  const bucketTotals = buckets
    .map(bucket => ({
      title: bucket.title,
      total: bucket.items.reduce((acc, cur) => acc + cur.value, 0),
    }))
    .sort((a, b) => b.total - a.total);

  let chartSizeIndex = 0;
  const chartSizesDict = { [bucketTotals[0].title]: chartSizes[chartSizeIndex] };

  for (let i = 1; i < bucketTotals.length; i += 1) {
    const currentTotal = bucketTotals[i].total;
    const previousTotal = bucketTotals[i - 1].total;

    if (Math.abs((currentTotal - previousTotal) / currentTotal) > CHART_SIZE_TOLERANCE) {
      chartSizeIndex += 1;
    }

    chartSizesDict[bucketTotals[i].title] = chartSizes[chartSizeIndex];
  }

  return chartSizesDict;
};
